import React, { useState, useEffect } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Typography,
  Paper,
  Box,
  Skeleton,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { API_PATHS } from '../../utils/constants';

/**
 * Component to display the status change history of a ticket as a timeline
 */
const StatusHistoryTimeline = ({ ticketId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatusHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token available for status history retrieval');
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch the status history from the API
      const response = await axios.get(
        `${API_PATHS.TICKETS}/status/${ticketId}/history`,
        { headers }
      );
      
      if (response.data) {
        setHistory(response.data);
      } else {
        setHistory([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching ticket status history:', err);
      setError('Failed to load status history. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      fetchStatusHistory();
    }
  }, [ticketId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'RESOLVED':
        return 'success.main';
      case 'IN_PROGRESS':
        return 'info.main';
      case 'REJECTED':
      case 'INCOMPLETE':
        return 'error.main';
      case 'MODIFICATION_REQUIRED':
        return 'warning.main';
      default:
        return 'grey.500';
    }
  };

  const getStatusIcon = (statusChange) => {
    const { newStatus } = statusChange;
    
    if (newStatus === 'RESOLVED') {
      return <SuccessIcon />;
    } else if (newStatus === 'REJECTED' || 
               newStatus === 'INCOMPLETE' || 
               newStatus === 'MODIFICATION_REQUIRED') {
      return <ErrorIcon />;
    } else {
      return <InfoIcon />;
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (e) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" gutterBottom>Status History</Typography>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', my: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ ml: 2, width: '100%' }}>
              <Skeleton width="40%" height={24} />
              <Skeleton width="60%" height={24} />
              <Skeleton width="80%" height={24} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" gutterBottom>Status History</Typography>
          <Tooltip title="Retry">
            <IconButton onClick={fetchStatusHistory} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Paper 
          sx={{ 
            p: 2, 
            bgcolor: 'error.light', 
            color: 'error.contrastText',
            borderRadius: 1
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" gutterBottom>Status History</Typography>
        <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2">No status change history available.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="h6" gutterBottom>Status History</Typography>
      <Timeline position="alternate">
        {history.map((statusChange) => (
          <TimelineItem key={statusChange.id}>
            <TimelineOppositeContent sx={{ m: 'auto 0' }}>
              <Typography variant="body2" color="text.secondary">
                {formatDate(statusChange.changedAt)}
              </Typography>
              <Typography variant="caption" display="block">
                {statusChange.changedBy || 'System'}
              </Typography>
            </TimelineOppositeContent>
            
            <TimelineSeparator>
              <TimelineDot sx={{ bgcolor: getStatusColor(statusChange.newStatus) }}>
                {getStatusIcon(statusChange)}
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" component="span">
                  Status changed from&nbsp;
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="span"
                  sx={{ color: getStatusColor(statusChange.previousStatus) }}
                >
                  {statusChange.previousStatus.replace('_', ' ')}
                </Typography>
                
                <Typography variant="subtitle2" component="span">
                  &nbsp;<ArrowIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />&nbsp;
                </Typography>
                
                <Typography
                  variant="subtitle2"
                  component="span"
                  sx={{ color: getStatusColor(statusChange.newStatus) }}
                >
                  {statusChange.newStatus.replace('_', ' ')}
                </Typography>
                
                {statusChange.comments && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    "{statusChange.comments}"
                  </Typography>
                )}
              </Paper>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
};

export default StatusHistoryTimeline;
